/**
 * useCoachingModule — the Coaching module composable (Program-Modules epic, Wave 4:
 * the coaching RETROFIT onto the module framework). Dark behind PROGRAM_MODULES;
 * coaching's own ENABLE_COACHING stays its enable gate.
 *
 * The direct analogue of usePlaces (Wave 3). Wraps the Nexus coaching module-state
 * op behind a thin, typed API and — crucially — UPGRADES the pre-existing 'coach'
 * thin-wrap descriptor (from W2) into a FULL module: panel + policyLoader + nudge +
 * onboardingPhase. Once registered, the shared trigger runner (useModuleTriggers,
 * already instantiated in TimelineBar for Places) picks coaching up automatically —
 * the nudge becomes POLICY-DRIVEN, not coaching-specific code.
 *
 *   - loadCoachingModuleState(programId) → server-derived {enabled, policy, state}
 *   - loadPolicy(programId)              → folds that into a ModulePolicy (rigidity in)
 *   - registerDescriptor()              → attaches the hooks onto the 'coach' key
 *
 * The loader SILENT-DEGRADES to a disabled ({ enabled: false }) payload on any error,
 * so a missing/failing backend never breaks the host — the legacy pill/dialog paths
 * are untouched by this module.
 *
 * The panel is the EXISTING ParticipantCoachingDialog; the onboarding phase is the
 * EXISTING aipm-onboarding/CoachingPhase.vue. Mirrors usePlaces's client-access
 * pattern (useNexusClient() + the gql ops in services/graphql/nexus/coaching.ts).
 */

import type { Component } from 'vue';
import { useNexusClient } from '@/composables/useApolloClients';
import { registerModuleDescriptor, type ModulePolicy } from '@/composables/useProgramModules';
import {
  COACHING_MODULE_STATE_QUERY,
  type CoachingModuleState,
  type CoachingModulePolicy
} from '@/services/graphql/nexus/coaching';

// ============================================================
// Degraded defaults (silent-degrade targets)
// ============================================================

const DISABLED_POLICY: CoachingModulePolicy = { enabled: false };

const DISABLED_MODULE_STATE: CoachingModuleState = {
  enabled: false,
  policy: DISABLED_POLICY,
  state: 'idle'
};

// ============================================================
// Pure mapper (no Apollo / no component context required)
// ============================================================

/**
 * Fold a CoachingModuleState into the framework's open-ended ModulePolicy so the
 * shared trigger runner can read enabled/required/rigidity/inAppNudge PLUS the
 * server-derived `state` (nudge.deriveState is a pass-through of `state`). Mirrors
 * usePlaces.mapStateToPolicy.
 *
 * `rigidity` is resolved SERVER-side (required ? (allowBookLater ? 'soft' : 'hard')
 * : 'soft'), so a required + !allowBookLater coaching policy arrives as `rigidity:
 * 'hard'` → the runner re-pops it every load (isHardRequired). We defensively
 * re-derive it here too when the server omits it, so the rigidity contract holds
 * even against an older backend.
 */
/** Resolve rigidity server-side-equivalently: required + !allowBookLater = hard, else soft. */
function deriveRigidity(p: CoachingModulePolicy): 'soft' | 'hard' {
  if (!p.required) return 'soft';
  return p.allowBookLater ? 'soft' : 'hard';
}

export function mapStateToPolicy(
  moduleState: CoachingModuleState | null | undefined
): ModulePolicy {
  const s = moduleState ?? DISABLED_MODULE_STATE;
  const p = s.policy ?? DISABLED_POLICY;
  const rigidity = p.rigidity ?? deriveRigidity(p);
  return {
    enabled: !!s.enabled && p.enabled !== false,
    required: p.required,
    rigidity,
    inAppNudge: p.inAppNudge,
    smsNudge: p.smsNudge,
    allowBookLater: p.allowBookLater,
    cadence: p.cadence,
    expectedCallsPerParticipant: p.expectedCallsPerParticipant,
    // Folded server-derived field (deriveState reads `state`).
    state: s.state
  };
}

// ============================================================
// Composable
// ============================================================

export function useCoachingModule(providedClient?: ReturnType<typeof useNexusClient>) {
  // Accept a pre-resolved client so async callers (useAIPMOnboarding.getCoachingModule runs
  // after awaits, outside component setup) can pass a synchronously-cached client. Resolving
  // fresh via useNexusClient() there throws "Apollo client with id nexus not found".
  const client = providedClient ?? useNexusClient();

  async function loadCoachingModuleState(programId: string): Promise<CoachingModuleState> {
    if (!programId) return DISABLED_MODULE_STATE;
    try {
      const { data } = await client.query({
        query: COACHING_MODULE_STATE_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.coachingModuleState as CoachingModuleState) ?? DISABLED_MODULE_STATE;
    } catch {
      // Silent-degrade: a failing/absent backend must never break the host.
      return DISABLED_MODULE_STATE;
    }
  }

  /**
   * The policyLoader the shared trigger runner iterates: loads the server module
   * state and maps it into a ModulePolicy (with `state` folded in + rigidity resolved).
   */
  async function loadPolicy(programId: string): Promise<ModulePolicy> {
    const moduleState = await loadCoachingModuleState(programId);
    return mapStateToPolicy(moduleState);
  }

  /**
   * Attach the Coaching module's capability hooks onto the 'coach' descriptor key.
   * Additive + idempotent (registerModuleDescriptor is a replace), behind
   * PROGRAM_MODULES at the call site. Closes over THIS instance's loaders so the
   * policyLoader reuses the already-bound Nexus client. EXACTLY mirrors
   * usePlaces().registerDescriptor().
   */
  function registerDescriptor(): void {
    registerModuleDescriptor('coach', {
      // The host opens the coach panel via onModuleSelect('coach') → openCoachingPanel;
      // the runner's auto-open routes through that same dispatch shim. Declaring the
      // panel marks 'coach' a FULL module. Same ParticipantCoachingDialog either path.
      panel: (() =>
        import(
          '@/components/organisms/coaching/ParticipantCoachingDialog.vue'
        )) as unknown as () => Promise<Component>,
      policyLoader: loadPolicy,
      nudge: {
        // Pass-through: deriveState runs SERVER-side; we surface `policy.state`.
        deriveState: (policy: ModulePolicy) =>
          (policy.state as 'idle' | 'due' | 'overdue') ?? 'idle'
      },
      onboardingPhase: {
        component: (() =>
          import(
            '@/components/organisms/aipm-onboarding/CoachingPhase.vue'
          )) as unknown as () => Promise<Component>,
        // Show the phase when the program has coaching enabled AND the participant hasn't
        // already booked. Reads the onboarding state shape (coachingPolicy.enabled or an
        // explicit coachingEnabled flag) and SKIPS when `coachingAlreadyBooked` is set —
        // the founder decision to not re-prompt scheduling for an already-booked session.
        // (The effective gate is useAIPMOnboarding.shouldShowCoaching, which loads that
        // flag from coachingModuleState; this mirror keeps the framework path consistent.)
        //
        // SETTINGS HOME: the coaching config surface (cadence, inAppNudge/smsNudge nudge
        // channels, allowBookLater, required) lives on AutoPilot as the program coaching
        // policy — SetProgramCoachingPolicyInput / programCoachingPolicy. Transparency:
        // onboarding skips when a session is booked; after that booked call finishes the
        // mid-program CADENCE nudge (computeCadenceState) resumes on the configured cadence.
        shouldShow: (ctx: unknown) => {
          const c = ctx as {
            coachingEnabled?: boolean;
            coachingPolicy?: { enabled?: boolean } | null;
            coachingAlreadyBooked?: boolean;
          } | null;
          if (c?.coachingAlreadyBooked === true) return false;
          return !!(c && (c.coachingEnabled || c.coachingPolicy?.enabled));
        }
      }
    });
  }

  return {
    loadCoachingModuleState,
    loadPolicy,
    registerDescriptor,
    mapStateToPolicy
  };
}

export default useCoachingModule;
